// PlainDropdown — a custom (non-native) dropdown so pickers match the app's
// styling instead of the OS menu. Closes on outside click / Escape.
//
// options accepts either plain strings (value === label) or { value, label }
// objects, so it works for both a fixed vocabulary and a keyed list (e.g. a
// staff picker where the value is an id but the label is a name).

import { useState, useRef, useEffect } from 'react';

export default function PlainDropdown({ value, onChange, options, placeholder = 'Select…', style, triggerClassName = 'input', triggerStyle, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const opts = (options || []).map(o => (typeof o === 'string' ? { value: o, label: o } : o));
  const selectedLabel = opts.find(o => o.value === value)?.label;
  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button type="button" className={triggerClassName} onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, cursor: 'pointer', textAlign: 'left', ...triggerStyle }}>
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selectedLabel ? 'var(--text)' : 'var(--text-3)' }}>
          {selectedLabel || placeholder}
        </span>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-3)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div role="listbox" className="plain-pop dd-shell">
          {opts.map(opt => (
            <button key={opt.value} type="button" role="option" aria-selected={opt.value === value} onClick={() => pick(opt.value)}
              className={`dd-item${opt.value === value ? ' selected' : ''}`}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
